import {Switch} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isTrue} from 'ramda-adjunct';

import {selectCurrentUserInfo} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../../../../constants/DEFAULT_CURRENCY_CODE';
import {sourcingFilterConstants} from '../../../../../../constants/sourcingFilterConstants';
import {SourcingType, useSourcingType} from '../../../../../../hooks/useSourcingType';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {SourcingSlider2} from '../SourcingSlider2/SourcingSlider2';
import {SourcingSlider} from '../SourcingSlider/SourcingSlider';

export function FilterStatistics(props: TestIdProps) {
  const sourcingType = useSourcingType();
  const {filters, onUpdateFilters} = useFilters();

  const selectedCurrency =
    useSelector(selectCurrentUserInfo)?.data?.settings.currency ?? DEFAULT_CURRENCY_CODE;

  return (
    <VStack spacing={8}>
      <Grid columns={[1, 2, 4]} spacing={8} data-testid="block.filters-statistics">
        <SourcingSlider
          label={`${i18n.t('general.labels.priceWithVat')} (${selectedCurrency})`}
          min={0}
          max={sourcingFilterConstants.PRICE_MAX}
          marks={sourcingFilterConstants.PRICE_MARKS}
          hasMaxPlus
          value={filters.priceCZK}
          onChange={(value) => onUpdateFilters('priceCZK', value)}
          data-testid={suffixTestId('priceWithVat', props)}
        />

        <SourcingSlider
          label={i18n.t('entity.vehicle.labels.margin')}
          labelTooltip={i18n.t('entity.vehicle.labels.marginTooltip')}
          step={filters.isMarginAbsolute ? 1000 : 1}
          min={
            filters.isMarginAbsolute
              ? sourcingFilterConstants.MARGIN_LIMIT_MIN
              : sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MIN
          }
          max={
            filters.isMarginAbsolute
              ? sourcingFilterConstants.MARGIN_LIMIT_MAX
              : sourcingFilterConstants.MARGIN_PERCENT_LIMIT_MAX
          }
          hasMaxPlus={filters.isMarginAbsolute}
          value={filters.isMarginAbsolute ? filters.margin : filters.marginPercent}
          onChange={(value) =>
            onUpdateFilters(filters.isMarginAbsolute ? 'margin' : 'marginPercent', value)
          }
          segmentOptions={[selectedCurrency, '%']}
          segmentValue={filters.isMarginAbsolute ? selectedCurrency : '%'}
          onSegmentChange={(value) =>
            onUpdateFilters('isMarginAbsolute', value === selectedCurrency)
          }
          data-testid={suffixTestId('margin', props)}
        />

        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <SourcingSlider
            label={i18n.t('entity.vehicle.labels.daysOnDisplay')}
            labelTooltip={i18n.t('entity.vehicle.labels.daysOnDisplayTooltip')}
            min={0}
            max={sourcingFilterConstants.DAYS_ON_DISPLAY}
            hasMaxPlus
            value={filters.daysOnDisplay}
            onChange={(value) => onUpdateFilters('daysOnDisplay', value)}
            data-testid={suffixTestId('daysOnDisplay', props)}
          />
        </Show>

        <SourcingSlider
          label={i18n.t('entity.vehicle.labels.liquidity')}
          labelTooltip={i18n.t('entity.vehicle.labels.liquidityTooltip')}
          step={12}
          min={0}
          max={sourcingFilterConstants.LIQUIDITY_MAX}
          hasMaxPlus
          value={filters.liquidity}
          onChange={(value) => onUpdateFilters('liquidity', value)}
          data-testid={suffixTestId('liquidity', props)}
        />

        <Show whenFeatureEnabled={featureFlags.SOURCING_FILTER_POSITION}>
          <SourcingSlider2
            label={i18n.t('entity.vehicle.labels.pricePosition')}
            labelTooltip={i18n.t('entity.vehicle.labels.pricePositionTooltip')}
            min={0}
            max={100}
            minLabel={i18n.t('entity.vehicle.labels.cheapest')}
            maxLabel={i18n.t('entity.vehicle.labels.mostExpensive')}
            value={filters.pricePosition}
            onChange={(value) => onUpdateFilters('pricePosition', value)}
            data-testid={suffixTestId('pricePosition', props)}
          />
        </Show>
      </Grid>

      <Switch
        value={isTrue(filters.reclaimableVat)}
        onChange={(checked) => onUpdateFilters('reclaimableVat', checked)}
        label={i18n.t('general.labels.reclaimableVAT')}
        data-testid={suffixTestId('reclaimableVat', props)}
      />
    </VStack>
  );
}
